import React, { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import {
  ArrowUp, ArrowDown, TrendingUp, Target, PoundSterling, Users,
  Signal, Activity, RefreshCw, Maximize2, X, AlertTriangle, ChevronsRight,
} from "lucide-react";

const REFRESH_MS = 60_000;

const gbp = (n) => {
  const num = Number(n) || 0;
  return "£" + Math.round(num).toLocaleString("en-GB");
};
const gbpDec = (n) => {
  const num = Number(n) || 0;
  return "£" + num.toLocaleString("en-GB", { minimumFractionDigits: 0, maximumFractionDigits: 0 });
};
const compact = (n) => {
  const num = Number(n) || 0;
  if (num >= 1_000_000) return (num / 1_000_000).toFixed(1).replace(/\.0$/, "") + "M";
  if (num >= 1_000) return (num / 1_000).toFixed(1).replace(/\.0$/, "") + "k";
  return String(Math.round(num));
};

export default function TvDashboard() {
  const nav = useNavigate();
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [lastRefresh, setLastRefresh] = useState(null);
  const [clock, setClock] = useState(new Date());
  const containerRef = useRef(null);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/stats/tv-dashboard");
      setData(data);
      setError(null);
      setLastRefresh(new Date());
    } catch (e) {
      setError(e?.response?.data?.detail || "Failed to load dashboard");
    }
  }, []);

  useEffect(() => {
    if (user && user.role !== "admin") { nav("/"); return; }
    load();
    const iv = setInterval(load, REFRESH_MS);
    const clockIv = setInterval(() => setClock(new Date()), 1000);
    return () => { clearInterval(iv); clearInterval(clockIv); };
  }, [load, user, nav]);

  // Keyboard shortcuts: F = fullscreen, R = manual refresh, Esc = exit fullscreen
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "f" || e.key === "F") {
        if (document.fullscreenElement) document.exitFullscreen();
        else containerRef.current?.requestFullscreen?.();
      } else if (e.key === "r" || e.key === "R") {
        load();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [load]);

  const quotaBadgeColor = useMemo(() => {
    const p = data?.quotaPct || 0;
    if (p >= 100) return "text-emerald-300 bg-emerald-500/15 border-emerald-500/30";
    if (p >= 60) return "text-amber-300 bg-amber-500/15 border-amber-500/30";
    return "text-rose-300 bg-rose-500/15 border-rose-500/30";
  }, [data?.quotaPct]);

  const revMax = useMemo(() => {
    if (!data?.revenueTrend?.length) return 1;
    return Math.max(1, ...data.revenueTrend.map((r) => r.value));
  }, [data?.revenueTrend]);

  const pipelineMax = useMemo(() => {
    if (!data?.pipelineStages?.length) return 1;
    return Math.max(1, ...data.pipelineStages.map((s) => s.value));
  }, [data?.pipelineStages]);

  return (
    <div
      ref={containerRef}
      className="fixed inset-0 bg-[#0A0F1A] text-white overflow-hidden flex flex-col"
      data-testid="tv-dashboard"
      style={{
        fontFeatureSettings: '"tnum" 1, "ss01" 1',
        backgroundImage: "radial-gradient(1200px 600px at 20% -10%, hsl(36 95% 52% / 0.10), transparent 60%), radial-gradient(900px 500px at 100% 100%, hsl(36 95% 52% / 0.06), transparent 60%)",
      }}
    >
      {/* Grain overlay */}
      <div className="absolute inset-0 pointer-events-none opacity-[0.035]" style={{ backgroundImage: "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='140' height='140' viewBox='0 0 140 140'><filter id='n'><feTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='2'/></filter><rect width='140' height='140' filter='url(%23n)'/></svg>\")" }} />

      {/* Top bar */}
      <header className="relative flex items-center justify-between px-8 pt-6 pb-4 shrink-0">
        <div className="flex items-center gap-3">
          <img src="/brand/soubor-one-mark.png" alt="" className="w-10 h-10 rounded-lg"/>
          <div>
            <div className="text-[11px] uppercase tracking-[0.3em] text-white/40 font-semibold">Soubor One · Command Deck</div>
            <div className="text-[15px] font-semibold tracking-tight">
              {clock.toLocaleDateString("en-GB", { weekday: "long", day: "numeric", month: "long", year: "numeric" })}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-6">
          {lastRefresh && (
            <div className="text-[11px] text-white/50 flex items-center gap-1.5" data-testid="tv-refresh-indicator">
              <RefreshCw className={`w-3 h-3 ${data?.cacheHit ? "opacity-40" : "opacity-100"}`} strokeWidth={2}/>
              Updated {lastRefresh.toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit", second: "2-digit" })}
              {data?.stale && <span className="ml-2 px-1.5 py-0.5 rounded bg-amber-500/20 text-amber-300 text-[9px] uppercase tracking-widest">Stale</span>}
            </div>
          )}
          <div className="tabular-nums font-bold text-[22px] tracking-tight">{clock.toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" })}</div>
          <button onClick={() => { if (document.fullscreenElement) document.exitFullscreen(); else containerRef.current?.requestFullscreen?.(); }} className="p-2 rounded-lg bg-white/5 hover:bg-white/10 transition-colors" title="Fullscreen (F)" data-testid="tv-fullscreen-btn">
            <Maximize2 className="w-4 h-4"/>
          </button>
          <button onClick={() => nav("/")} className="p-2 rounded-lg bg-white/5 hover:bg-white/10 transition-colors" title="Close" data-testid="tv-close-btn">
            <X className="w-4 h-4"/>
          </button>
        </div>
      </header>

      {error && (
        <div className="mx-8 mb-3 flex items-center gap-2 text-[12px] text-rose-300 bg-rose-500/10 border border-rose-500/30 rounded-lg px-3 py-2">
          <AlertTriangle className="w-4 h-4"/> {error}
        </div>
      )}

      {!data ? (
        <div className="flex-1 flex items-center justify-center text-white/40">Loading command deck…</div>
      ) : (
        <div className="relative flex-1 grid grid-cols-12 grid-rows-[auto_1fr_1fr] gap-4 px-8 pb-6 min-h-0">
          {/* KPI strip */}
          <KpiTile
            className="col-span-3"
            icon={<PoundSterling className="w-5 h-5"/>}
            label="Monthly recurring"
            value={gbpDec(data.mrr)}
            sub={data.mrrPending > 0 ? `+${gbp(data.mrrPending)} pending` : `Quota ${gbp(data.monthlyQuota)}/mo`}
            accent
            testid="tv-kpi-mrr"
          />
          <KpiTile
            className="col-span-3"
            icon={<TrendingUp className="w-5 h-5"/>}
            label="Open pipeline"
            value={gbpDec(data.pipelineValue)}
            sub={`${data.pipelineStages.reduce((s, x) => s + x.count, 0)} open leads · ${data.wonThisMonth} won this month`}
            testid="tv-kpi-pipeline"
          />
          <KpiTile
            className="col-span-3"
            icon={<Signal className="w-5 h-5"/>}
            label="Outstanding"
            value={gbpDec(data.outstanding)}
            sub={data.overdue > 0 ? `${gbp(data.overdue)} overdue · ${data.openTickets} open tickets` : `${data.openTickets} open tickets`}
            danger={data.overdue > 0}
            testid="tv-kpi-outstanding"
          />
          <div className="col-span-3 relative rounded-2xl border border-white/10 bg-white/[0.03] backdrop-blur-sm p-5 flex flex-col justify-between" data-testid="tv-kpi-quota">
            <div className="flex items-start justify-between">
              <div>
                <div className="text-[11px] uppercase tracking-widest text-white/40 font-semibold flex items-center gap-1.5">
                  <Target className="w-3 h-3"/> Quota progress
                </div>
                <div className="text-[46px] font-bold tabular-nums leading-none mt-2 tracking-tight">{data.quotaPct}%</div>
                <div className="text-[11px] text-white/50 mt-1.5">{gbp(data.mrr)} of {gbp(data.monthlyQuota)}</div>
              </div>
              <span className={`text-[10px] uppercase tracking-widest font-bold px-2 py-1 rounded border ${quotaBadgeColor}`}>
                {data.quotaPct >= 100 ? "Smashed" : data.quotaPct >= 60 ? "On track" : "Push"}
              </span>
            </div>
            <div className="mt-4 h-2 rounded-full bg-white/5 overflow-hidden">
              <div className="h-full rounded-full transition-all duration-700" style={{ width: `${Math.min(data.quotaPct, 100)}%`, background: "linear-gradient(90deg, hsl(36 95% 52%), hsl(20 90% 55%))" }}/>
            </div>
          </div>

          {/* Pipeline funnel */}
          <Panel className="col-span-5" title="Sales pipeline" subtitle="Open leads by stage" icon={<ChevronsRight className="w-4 h-4"/>}>
            <div className="mt-3 space-y-3.5" data-testid="tv-pipeline">
              {data.pipelineStages.map((s) => (
                <div key={s.name}>
                  <div className="flex items-baseline justify-between mb-1.5">
                    <div className="text-[13px] font-semibold text-white/85">{s.name}</div>
                    <div className="flex items-baseline gap-3">
                      <span className="text-[11px] text-white/40 tabular-nums">{s.count} {s.count === 1 ? "lead" : "leads"}</span>
                      <span className="text-[18px] font-bold tabular-nums">{gbp(s.value)}</span>
                    </div>
                  </div>
                  <div className="h-2 rounded-full bg-white/5 overflow-hidden">
                    <div className="h-full rounded-full transition-all duration-700" style={{ width: `${(s.value / pipelineMax) * 100}%`, background: "linear-gradient(90deg, hsl(36 95% 52% / 0.9), hsl(36 95% 62% / 0.6))" }}/>
                  </div>
                </div>
              ))}
              {data.pipelineStages.every((s) => s.count === 0) && (
                <div className="text-[12px] text-white/40 italic">No open leads. Time to prospect.</div>
              )}
            </div>
          </Panel>

          {/* Revenue trend */}
          <Panel className="col-span-7" title="Revenue trend" subtitle="Paid invoices · last 6 months" icon={<TrendingUp className="w-4 h-4"/>}>
            <div className="mt-4 flex-1 grid grid-cols-6 gap-3 items-end" style={{ minHeight: 160 }} data-testid="tv-revenue-chart">
              {data.revenueTrend.map((r, i) => (
                <div key={r.month + i} className="flex flex-col items-center gap-2">
                  <div className="text-[11px] text-white/50 tabular-nums">{gbp(r.value)}</div>
                  <div className="w-full rounded-t-lg transition-all duration-700 relative overflow-hidden" style={{ height: `${(r.value / revMax) * 140 + 8}px`, background: "linear-gradient(180deg, hsl(36 95% 52% / 0.9), hsl(36 95% 52% / 0.15))" }}>
                    <div className="absolute inset-0 rounded-t-lg border-t border-l border-r border-white/10"/>
                  </div>
                  <div className="text-[11px] uppercase tracking-widest text-white/40 font-semibold">{r.month}</div>
                </div>
              ))}
            </div>
          </Panel>

          {/* Top accounts */}
          <Panel className="col-span-5" title="Top accounts" subtitle="Ranked by MRR · 30-day delta" icon={<Users className="w-4 h-4"/>}>
            <div className="mt-3 space-y-2.5" data-testid="tv-top-accounts">
              {data.topAccounts.length === 0 && (
                <div className="text-[12px] text-white/40 italic">No paying accounts yet.</div>
              )}
              {data.topAccounts.map((a, i) => (
                <div key={a.id} className="flex items-center gap-3 rounded-xl bg-white/[0.03] border border-white/[0.06] px-3.5 py-2.5">
                  <div className="tabular-nums text-[13px] font-bold text-white/40 w-5">{i + 1}</div>
                  <img src={a.logo_url || "/brand/soubor-one-mark.png"} alt="" className="w-9 h-9 rounded-lg object-cover ring-1 ring-white/10 shrink-0" onError={(e) => { e.currentTarget.src = "/brand/soubor-one-mark.png"; }}/>
                  <div className="flex-1 min-w-0">
                    <div className="text-[14px] font-semibold truncate">{a.name}</div>
                    <div className="text-[11px] text-white/45">MRR</div>
                  </div>
                  <div className="text-right">
                    <div className="text-[18px] font-bold tabular-nums">{gbp(a.value)}</div>
                    {a.deltaPct !== 0 && (
                      <div className={`text-[11px] font-semibold inline-flex items-center gap-0.5 ${a.deltaPct > 0 ? "text-emerald-400" : "text-rose-400"}`}>
                        {a.deltaPct > 0 ? <ArrowUp className="w-3 h-3"/> : <ArrowDown className="w-3 h-3"/>}
                        {Math.abs(a.deltaPct)}%
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </Panel>

          {/* Apps grid */}
          <Panel className="col-span-7" title="Live apps" subtitle="30-day GoodBarber telemetry" icon={<Activity className="w-4 h-4"/>}>
            <div className="mt-3 grid grid-cols-2 gap-3" data-testid="tv-apps-grid">
              {data.apps.map((a) => (
                <div key={a.id} className="relative rounded-xl bg-white/[0.03] border border-white/[0.06] p-4 flex items-center gap-4">
                  <img src={a.icon_url || "/brand/soubor-one-mark.png"} alt="" className="w-14 h-14 rounded-xl object-cover ring-1 ring-white/10 shrink-0" onError={(e) => { e.currentTarget.src = "/brand/soubor-one-mark.png"; }}/>
                  <div className="flex-1 min-w-0">
                    <div className="text-[14px] font-semibold truncate">{a.name}</div>
                    <div className="flex items-baseline gap-3 mt-1">
                      <div>
                        <div className="text-[10px] uppercase tracking-widest text-white/40 font-semibold">Active users</div>
                        <div className="text-[22px] font-bold tabular-nums leading-none mt-0.5">{compact(a.activeUsers)}</div>
                      </div>
                      <div className="ml-auto text-right">
                        <div className="text-[10px] uppercase tracking-widest text-white/40 font-semibold">Sessions</div>
                        <div className="text-[16px] font-semibold tabular-nums">{compact(a.sessions)}</div>
                      </div>
                    </div>
                    <div className="mt-2 flex items-center gap-2 text-[11px] text-white/50">
                      <div className="flex-1 h-1.5 rounded-full bg-white/5 overflow-hidden">
                        <div className="h-full" style={{ width: `${Math.min(a.engagementPct, 100)}%`, background: "linear-gradient(90deg, hsl(36 95% 52%), hsl(20 90% 55%))" }}/>
                      </div>
                      <span className="tabular-nums font-semibold text-white/70">{a.engagementPct}%</span>
                      <span className="text-white/30">engagement</span>
                    </div>
                  </div>
                </div>
              ))}
              {data.apps.length === 0 && (
                <div className="col-span-2 text-[12px] text-white/40 italic">No apps registered yet.</div>
              )}
            </div>
          </Panel>
        </div>
      )}

      {/* Footer hint */}
      <footer className="relative shrink-0 px-8 py-2 flex items-center justify-between text-[10px] text-white/30 uppercase tracking-widest">
        <span>Auto-refresh · 60s</span>
        <span>Press F to go fullscreen · R to refresh · Esc to exit</span>
      </footer>
    </div>
  );
}

function KpiTile({ className, icon, label, value, sub, accent, danger, testid }) {
  return (
    <div className={`relative rounded-2xl border p-5 flex flex-col justify-between overflow-hidden ${accent ? "border-[hsl(36_95%_52%_/_0.4)] bg-gradient-to-br from-[hsl(36_95%_52%_/_0.10)] via-white/[0.03] to-white/[0.02]" : danger ? "border-rose-500/30 bg-rose-500/[0.05]" : "border-white/10 bg-white/[0.03]"} backdrop-blur-sm ${className || ""}`} data-testid={testid}>
      {accent && <div className="absolute -top-16 -right-10 w-56 h-56 rounded-full pointer-events-none" style={{ background: "radial-gradient(circle, hsl(36 95% 52% / 0.25), transparent 65%)" }}/>}
      <div className="relative flex items-center gap-1.5 text-[11px] uppercase tracking-widest font-semibold text-white/40">
        <span className={accent ? "text-[hsl(36_95%_65%)]" : danger ? "text-rose-400" : "text-white/50"}>{icon}</span>
        <span>{label}</span>
      </div>
      <div className="relative">
        <div className="text-[46px] font-bold tabular-nums tracking-tight leading-none">{value}</div>
        {sub && <div className="text-[11.5px] text-white/50 mt-1.5">{sub}</div>}
      </div>
    </div>
  );
}

function Panel({ className, title, subtitle, icon, children }) {
  return (
    <div className={`relative rounded-2xl border border-white/10 bg-white/[0.03] backdrop-blur-sm p-5 flex flex-col min-h-0 ${className || ""}`}>
      <div className="flex items-start justify-between shrink-0">
        <div>
          <div className="text-[11px] uppercase tracking-widest font-semibold text-white/40 flex items-center gap-1.5">
            <span className="text-white/50">{icon}</span> {title}
          </div>
          {subtitle && <div className="text-[11px] text-white/35 mt-0.5">{subtitle}</div>}
        </div>
      </div>
      <div className="flex-1 min-h-0 flex flex-col">{children}</div>
    </div>
  );
}
